import { Box, Divider, type SxProps, type Theme } from '@mui/material';
import { type ReactNode, Children, isValidElement } from 'react';
import { useScreenSize } from '@/hooks/use-screen-size';

interface DashboardWidgetsGridProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
  showDividers?: boolean;
  removeWidgetBorders?: boolean;
  wideScreenHeightScale?: number;
}

/**
 * Grid container for dashboard widgets.
 * - isWideScreen (>= 860px): 2 widgets side by side
 * - smaller screens: 1 widget takes full width
 * Each widget size is managed here:
 * - height: calc((100vh - 64px) / 2) on wide screens
 * - width: full on small screens, half on wide screens
 */
export default function DashboardWidgetsGrid({
  children,
  sx,
  showDividers = false,
  removeWidgetBorders = false,
  wideScreenHeightScale = 1,
}: DashboardWidgetsGridProps) {
  const { isWideScreen } = useScreenSize({ breakpoint: 860 });
  const isTwoColumn = isWideScreen;

  // Process children to add dividers if needed
  const processedChildren = showDividers
    ? Children.toArray(children).map((child, index) => {
        if (!isValidElement(child)) return child;

        // Add divider after each child except the last one
        if (index < Children.count(children) - 1) {
          return (
            <>
              {child}
              <Divider
                orientation={isTwoColumn ? 'vertical' : 'horizontal'}
                flexItem
                sx={{
                  mx: isTwoColumn ? 1 : 0,
                  my: isTwoColumn ? 0 : 1,
                }}
              />
            </>
          );
        }

        return child;
      })
    : children;

  return (
    <Box
      sx={{
        'display': 'flex',
        'gap': showDividers ? 0 : 2,
        'flexDirection': isTwoColumn ? 'row' : 'column',
        'width': '100%',
        'flexWrap': isTwoColumn ? 'wrap' : 'nowrap',
        'alignItems': 'stretch',
        '& > *': {
          height: isTwoColumn ? `calc((100vh - 64px) * ${wideScreenHeightScale / 2})` : 'auto',
          width: isTwoColumn ? (showDividers ? 'calc((100% - 16px) / 2)' : 'calc(50% - 8px)') : '100%',
          flexShrink: 0,
          minWidth: 0,
          minHeight: isTwoColumn ? 300 * wideScreenHeightScale : 300,
          maxHeight: isTwoColumn ? 400 * wideScreenHeightScale : 400,
          // Remove borders from widgets if requested
          ...(removeWidgetBorders && {
            'border': 'none',
            // Also remove borders from nested Card components (used by Widget)
            '& .MuiCard-root': {
              border: 'none',
              boxShadow: 'none',
            },
          }),
        },
        ...sx,
      }}
    >
      {processedChildren}
    </Box>
  );
}
